import React, { useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import './ConnectBankModal.css';

const {
  FiArrowRight,
  FiLock,
  FiShield,
  FiX,
} = FiIcons;

export default function ConnectBankModal({
  isOpen,
  isLoading,
  onClose,
  onContinue,
}) {
  const dialogRef = useRef(null);
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = dialogRef.current?.querySelectorAll(
        'button:not([disabled])'
      );

      if (!focusableElements?.length) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('connect-bank-modal-open');
    cancelButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('connect-bank-modal-open');
    };
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <div
      className="connect-bank-modal-backdrop"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="connect-bank-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-bank-modal-title"
        aria-describedby="connect-bank-modal-description"
      >
        <div className="connect-bank-modal-header">
          <div className="connect-bank-modal-icon">
            <SafeIcon icon={FiShield} />
          </div>

          {!isLoading && (
            <button
              type="button"
              className="connect-bank-modal-close"
              onClick={onClose}
              aria-label="Close connect bank dialog"
            >
              <SafeIcon icon={FiX} />
            </button>
          )}
        </div>

        <div className="connect-bank-modal-content">
          <h2 id="connect-bank-modal-title">
            Connect your bank
          </h2>

          <p className="connect-bank-modal-subtitle">
            Securely connect your bank account to ProfitYO.
          </p>

          <p
            id="connect-bank-modal-description"
            className="connect-bank-modal-description"
          >
            Connect your bank account to securely import your
            banking activity into ProfitYO. You&apos;ll use Plaid
            to select your financial institution and sign in
            securely.
          </p>

          <div className="connect-bank-modal-security">
            <div className="connect-bank-modal-security-item">
              <div className="connect-bank-modal-security-icon">
                <SafeIcon icon={FiShield} />
              </div>

              <span>
                Your bank login credentials are handled securely
                by Plaid.
              </span>
            </div>

            <div className="connect-bank-modal-security-item">
              <div className="connect-bank-modal-security-icon">
                <SafeIcon icon={FiLock} />
              </div>

              <span>
                ProfitYO never sees or stores your bank password.
              </span>
            </div>

            <p className="connect-bank-modal-security-note">
              You can disconnect your bank account at any time.
            </p>
          </div>
        </div>

        <div className="connect-bank-modal-actions">
          <button
            ref={cancelButtonRef}
            type="button"
            className="connect-bank-modal-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>

          <button
            type="button"
            className="connect-bank-modal-continue"
            onClick={onContinue}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span
                  className="connect-bank-modal-spinner"
                  aria-hidden="true"
                />
                Preparing secure connection...
              </>
            ) : (
              <>
                Continue with Plaid
                <SafeIcon icon={FiArrowRight} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}